// oxlint-disable react-perf/jsx-no-new-object-as-prop
import { Box, Icon, useOpenClose } from '@palmetto/palmetto-components';

import { CollapsibleDetails } from './CollapsibleDetails';
import EventBy from './EventBy';

export interface UtilityBillExtractionProcessedEvent {
  performedBy: { displayName: string; organizationId?: string };
  data: {
    outcome: 'approved' | 'submittedForReview';
    checkFailures: string[];
    extractedData: {
      customerName: string | null;
      serviceAddress: string | null;
      utilityName: string | null;
      billDate: string | null;
      pastDueBalance: number | null;
      currentBalance: number | null;
      currentMonthGasCharges?: number | null;
      currentMonthCombinedCharges?: number | null;
      isNotUtilityBill: string | null;
    };
  };
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '—';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const DataRow = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <Box direction="row" childGap="sm">
    <Box as="span" fontWeight="bold" style={{ minWidth: '140px' }}>
      {label}:
    </Box>
    <Box as="span">{value ?? '—'}</Box>
  </Box>
);

const UtilityBillExtractionProcessed = ({ event }: { event: UtilityBillExtractionProcessedEvent }) => {
  const { isOpen, handleToggle } = useOpenClose();
  const { outcome, checkFailures, extractedData } = event.data;
  const isApproved = outcome === 'approved';

  return (
    <Box display="block" childGap="sm">
      <Box display="block">
        <Box as="span" display="inline-block" margin="0 2xs 0 0">
          <EventBy event={event} /> processed a utility bill extraction —{' '}
          <Box as="span" fontWeight="bold">
            {isApproved ? 'auto-approved' : 'added to queue'}
          </Box>
        </Box>
      </Box>

      {extractedData && (
        <CollapsibleDetails title="Extracted Data" isOpen={isOpen} onToggle={handleToggle}>
          {extractedData.isNotUtilityBill && (
            <Box
              direction="row"
              childGap="xs"
              alignItems="center"
              color="danger"
              fontWeight="bold"
              margin="0 0 xs 0"
            >
              <Icon name="c-warning" />
              <Box as="span">{extractedData.isNotUtilityBill}</Box>
            </Box>
          )}
          {!isApproved && checkFailures?.length > 0 && (
            <DataRow label="Failure Reason" value={checkFailures.join(', ')} />
          )}
          <DataRow label="Customer Name" value={extractedData.customerName} />
          <DataRow label="Service Address" value={extractedData.serviceAddress} />
          <DataRow label="Utility Company" value={extractedData.utilityName} />
          <DataRow label="Bill Date" value={extractedData.billDate} />
          <DataRow label="Past-Due Balance" value={formatCurrency(extractedData.pastDueBalance)} />
          <DataRow
            label={extractedData.currentMonthGasCharges != null ? 'Electric Balance' : 'Current Balance'}
            value={formatCurrency(extractedData.currentBalance)}
          />
          {extractedData.currentMonthGasCharges != null && (
            <DataRow label="Gas Charges" value={formatCurrency(extractedData.currentMonthGasCharges)} />
          )}
          {extractedData.currentMonthGasCharges != null && extractedData.currentMonthCombinedCharges != null && (
            <DataRow label="Combined Charges" value={formatCurrency(extractedData.currentMonthCombinedCharges)} />
          )}
        </CollapsibleDetails>
      )}
    </Box>
  );
};

export default UtilityBillExtractionProcessed;
